import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import { Grid, IconButton } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import { useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { IWorkAssignment } from 'wisestep-api';
import {
  EmptyTeamsAndUsersIcon,
  WsColors,
  WsDataGrid,
  WsListingLoader,
  WsTypoColors,
  WsTypography,
} from 'wisestep-base';

import NoDataFoundCard from '../../../components/noDataFoundCard';
import { onRecruiterNameClick } from '../../../features/candidateLayout/utils/CandidateHelper';
import SearchBar from '../../../features/teams/components/SearchBarWrapper';
import WsAvatar from '../../../features/teams/components/WsAvatar';
import PersonAvatar from '../../../images/Avatar/Person.svg';
import { useJobCRMContext } from '../context';

interface RecruiterAllocationProps {
  unAssignAllowed: boolean;
  unAssignRecruiter: (recruiter: IWorkAssignment) => void;
}

const RecruiterAllocation = ({ unAssignAllowed, unAssignRecruiter }: RecruiterAllocationProps) => {
  const { job } = useJobCRMContext();
  const [loading] = useState(false);
  const [page, setPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(50);
  const methods = useForm();
  const handlePage = (newPage: number) => setPage(newPage);

  const handlePageSize = (newPageSize: number) => {
    handlePage(0);
    setPageSize(newPageSize);
  };

  const data = useMemo(
    () =>
      (job?.assignedTo ?? [])?.map((recruiter) => ({
        id: recruiter?.recruiterId ?? '',
        recruiterName: recruiter?.recruiterName,
        recruiter: recruiter,
      })),
    [job?.assignedTo]
  );
  const columns: GridColDef[] = [
    {
      field: 'recruiterName',
      headerName: 'Recruiter',
      sortable: false,
      flex: 2.5,
      renderCell: (params) => (
        <Grid container alignItems="center" spacing={2}>
          <Grid item>
            <WsAvatar src={PersonAvatar} size="small" variant="circular" />
          </Grid>
          <Grid item>
            <WsTypography
              onClick={() => onRecruiterNameClick(params.row.id)}
              cursor="pointer"
              color={WsTypoColors.PRIMARY750}
            >
              {params.value}
            </WsTypography>
          </Grid>
        </Grid>
      ),
    },
    ...(unAssignAllowed
      ? [
          {
            field: 'recruiter',
            headerName: 'Un Assign',
            sortable: false,
            flex: 0.3,
            renderCell: (params) => (
              <IconButton onClick={() => unAssignRecruiter(params.value)}>
                <RemoveCircleIcon sx={{ fontSize: 20, color: WsColors.DarkRed }} />
              </IconButton>
            ),
          },
        ]
      : []),
  ];

  return (
    <Grid container rowGap={1}>
      <Grid item xs={12}>
        <FormProvider {...methods}>
          <SearchBar name="query" />
        </FormProvider>
      </Grid>
      <Grid item xs={12}>
        {loading ? (
          <WsListingLoader minHeight={400} />
        ) : (
          <>
            {data?.length ? (
              <WsDataGrid
                autoHeight
                rows={data}
                columns={columns}
                paginationMode="client"
                page={page ?? 0}
                onPageChange={handlePage}
                pageSize={pageSize ?? 20}
                onPageSizeChange={handlePageSize}
              />
            ) : (
              <NoDataFoundCard text="No results found" icon={<EmptyTeamsAndUsersIcon />} />
            )}
          </>
        )}
      </Grid>
    </Grid>
  );
};

export default RecruiterAllocation;
